import { ConfirmDialog } from '@/components/feature/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { useSettings } from '@/hooks/useSettings';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { AvatarDialog } from './avatar-tab/avatar-dialog';
import { ServiceAvatar, getServiceAvatarColumns } from './avatar-tab/columns';

const ServiceAvatarTab = () => {
  const { settings, updateSettings } = useSettings();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<ServiceAvatar | undefined>(undefined);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAddAvatar = (data: ServiceAvatar) => {
    if (!settings) return;
    const updatedAvatars = [...(settings.serviceAvatar ?? []), data];
    updateSettings({ ...settings, serviceAvatar: updatedAvatars });
    setDialogOpen(false);
  };

  const handleEditAvatar = (index: number, data: ServiceAvatar) => {
    if (!settings?.serviceAvatar) return;
    const updatedAvatars = settings.serviceAvatar.map((avatar, i) => (i === index ? data : avatar));
    updateSettings({ ...settings, serviceAvatar: updatedAvatars });
    setEditingIndex(null);
    setSelectedAvatar(undefined);
    setDialogOpen(false);
  };

  const handleDeleteAvatar = (index: number) => {
    if (!settings?.serviceAvatar) return;
    const updatedAvatars = settings.serviceAvatar.filter((_, i) => i !== index);
    updateSettings({ ...settings, serviceAvatar: updatedAvatars });
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleOpenDialog = (avatar?: ServiceAvatar, index?: number) => {
    setSelectedAvatar(avatar);
    setEditingIndex(index !== undefined ? index : null);
    setDialogOpen(true);
  };

  const handleMoveAvatar = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < (settings?.serviceAvatar?.length ?? 0)) {
      const newServiceAvatar = [...(settings?.serviceAvatar ?? [])];
      [newServiceAvatar[index], newServiceAvatar[newIndex]] = [
        newServiceAvatar[newIndex],
        newServiceAvatar[index],
      ];
      updateSettings({ serviceAvatar: newServiceAvatar });
    }
  };

  return (
    <Card className="border-none">
      <CardHeader>
        <CardTitle>Service Avatars</CardTitle>
        <CardDescription>Manage the avatars that can be used for your services.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-end pb-4">
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Avatar
          </Button>
        </div>

        <DataTable
          columns={getServiceAvatarColumns({
            onMoveAvatar: handleMoveAvatar,
            onEdit: handleOpenDialog,
            onDelete: index => {
              setDeleteIndex(index);
              setDeleteDialogOpen(true);
            },
            totalItems: settings?.serviceAvatar?.length ?? 0,
          })}
          data={settings?.serviceAvatar ?? []}
          hideSearch={true}
        />

        <AvatarDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={data =>
            editingIndex !== null ? handleEditAvatar(editingIndex, data) : handleAddAvatar(data)
          }
          initialData={selectedAvatar}
          isEditing={editingIndex !== null}
        />

        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Avatar"
          description="Are you sure you want to delete this avatar? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="destructive"
          onConfirm={() => deleteIndex !== null && handleDeleteAvatar(deleteIndex)}
        />
      </CardContent>
    </Card>
  );
};

export default ServiceAvatarTab;
